'use client';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useRelatedProducts } from '@eliadmin/core/src/hooks/products/use-related-products';
import { RelatedProductsStatus } from '@eliadmin/core/src/components/product/detail/related-products-status';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';
import { GoArrowLeft, GoArrowRight } from 'react-icons/go';
import { CarouselWall } from '@eliadmin/core/src/components/ui/carousel-wall';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { ProductCard } from '@eliadmin/core/src/components/product/product-card';
import { CarouselProgress } from '@eliadmin/core/src/components/ui/carousel-progress';

export const ProductRecomendedProducts = () => {
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const product = useVariantsStore(s => s.product);

  const options = {
    align: 'start' as const,
  };

  const carouselContext = useCarouselContext();

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex } = carouselContext;

  const related = useRelatedProducts(product.slug);
  const relatedProducts = related.products;
  const isRecommendedAccessoriesEnabled =
    !!templateSettings.product_detail?.product_recommended_accessories?.enabled;
  const accessories = isRecommendedAccessoriesEnabled ? (product.accessories ?? []) : [];

  const hasRelatedProducts = relatedProducts.length > 0;
  const hasAccessories = accessories.length > 0;

  // Display nothing if:
  // - there are no related products AND
  // - (recommended accessories are disabled OR there are no accessories)
  if (
    !hasRelatedProducts &&
    (!isRecommendedAccessoriesEnabled || !hasAccessories) &&
    !related.isLoading &&
    !related.isError
  ) {
    return null;
  }

  // Merge accessories (first) + related products and dedupe by id
  const mergedProducts = [...accessories, ...relatedProducts];

  const seenIds = new Set<number | string>();
  const productsToShow = mergedProducts.filter((item: any) => {
    const id = item?.id;
    if (id == null) return false;
    if (seenIds.has(id)) return false;
    seenIds.add(id);
    return true;
  });

  return (
    <>
      {' '}
      <section
        className={cn(
          'custom-section',
          'gap-8 bg-white py-8 sm:gap-10 sm:py-12 lg:gap-12 lg:py-[60px]',
        )}
      >
        <div className={cn('custom-container', 'flex items-center justify-between gap-4')}>
          <h2 className="font-oswald text-3xl font-medium uppercase text-black sm:text-4xl lg:text-5xl">
            Podobné produkty
          </h2>
          <CarouselControls
            className={{
              main: 'hidden gap-2 sm:flex',
              button:
                'h-12 w-12 rounded-none border-none bg-primary shadow-none hover:bg-primary/90',
            }}
            nextIcon={<GoArrowRight size={24} className="text-white" />}
            prevIcon={<GoArrowLeft size={24} className="text-white" />}
          />
        </div>
        <div className={cn('custom-container', 'relative overflow-visible')}>
          <CarouselWall className="bg-white" />
          <Carousel
            options={options}
            className={{
              slide: 'w-full max-w-[320px] sm:max-w-[340px] md:max-w-[360px] lg:max-w-[384px]',
              wrapper: 'w-full overflow-visible',
              container: 'w-full gap-4 overflow-visible sm:gap-5 lg:gap-6',
              main: 'w-full overflow-visible',
            }}
            slides={productsToShow.map(product => (
              <ProductCard product={product as any} key={product?.slug} />
            ))}
          />
        </div>
        <div
          className={cn(
            'custom-container',
            'flex items-center justify-between gap-4 pr-4 sm:hidden',
          )}
        >
          <CarouselProgress selectedIndex={selectedIndex} totalSlides={productsToShow.length} />
          <CarouselControls
            className={{
              main: 'flex gap-2',
              button:
                'max-h-10 min-h-10 min-w-10 max-w-10 rounded-none border-none bg-primary shadow-none hover:bg-primary/90',
            }}
            nextIcon={<GoArrowRight size={20} className="text-white" />}
            prevIcon={<GoArrowLeft size={20} className="text-white" />}
          />
        </div>
      </section>{' '}
      <div className="mx-auto w-full max-w-screen-2xl px-4">
        <RelatedProductsStatus
          loading={related.isLoading}
          error={related.isError}
          retry={() => {
            void related.refetch();
          }}
        />
      </div>{' '}
    </>
  );
};
